/**
 * Client-side utilities for pre-fetching and inlining images and fonts.
 * Used by collage/asset generation to avoid duplicate fetches from html-to-image.
 */

import { getFontEmbedCSS, toSvg } from "html-to-image";

/**
 * Wait for all <img> elements within a container to finish loading.
 */
export async function waitForImagesToLoad(
  element: HTMLElement
): Promise<void> {
  const images = element.querySelectorAll("img");
  const imagePromises = Array.from(images).map((img) => {
    if (img.complete && img.naturalWidth > 0) {
      return Promise.resolve();
    }
    // Force eager loading so off-screen images (e.g. hidden thumbnail
    // renderers) actually get fetched by the browser. Re-assign src to
    // ensure the browser starts the request.
    //
    // On WebKit, images far offscreen (e.g. positioned at -9999px) may
    // report complete=true with naturalWidth=0 even without loading="lazy",
    // so we always re-trigger the fetch when naturalWidth is 0.
    if (img.src && (img.loading === "lazy" || (img.complete && img.naturalWidth === 0))) {
      img.loading = "eager";
      const { src } = img;
      img.src = "";
      img.src = src;
    }
    return new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  });
  await Promise.all(imagePromises);
}

export type InlineImagesResult = {
  total: number;
  inlined: number;
  failed: number;
  failedUrls: string[];
  totalDataUrlBytes: number;
};

/**
 * Pre-fetch all unique images across multiple elements and replace their src
 * attributes with data URLs. Uses fetch + FileReader to preserve the original
 * image format (JPEG, etc.) — this keeps data URLs small, which is critical
 * because html-to-image embeds them inside an SVG data URI.
 */
export async function inlineImagesAsDataUrls(
  elements: (HTMLElement | null)[]
): Promise<InlineImagesResult> {
  const cache = new Map<string, string>();
  const allSrcs = new Set<string>();
  const failedUrls: string[] = [];

  for (const el of elements) {
    if (!el) continue;
    el.querySelectorAll("img").forEach((img) => {
      if (img.src && !img.src.startsWith("data:")) allSrcs.add(img.src);
    });
  }

  await Promise.all(
    Array.from(allSrcs).map(async (src) => {
      try {
        const res = await fetch(src, { mode: "cors" });
        if (!res.ok) {
          failedUrls.push(`${src} (HTTP ${res.status})`);
          return;
        }
        const blob = await res.blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        cache.set(src, dataUrl);
      } catch (e) {
        failedUrls.push(`${src} (${e instanceof Error ? e.message : "unknown error"})`);
      }
    })
  );

  let totalDataUrlBytes = 0;
  for (const el of elements) {
    if (!el) continue;
    el.querySelectorAll("img").forEach((img) => {
      const cached = cache.get(img.src);
      if (cached) {
        img.src = cached;
        totalDataUrlBytes += cached.length;
      }
    });
  }

  return {
    total: allSrcs.size,
    inlined: cache.size,
    failed: failedUrls.length,
    failedUrls,
    totalDataUrlBytes,
  };
}

/**
 * Wait for all <img> elements within a container to be fully decoded and
 * ready to render. Call this after inlineImagesAsDataUrls — replacing src
 * with a data URL triggers a new decode, and on iOS Safari this decode is
 * async. Without waiting, html-to-image's SVG foreignObject render may
 * capture images before they've been decoded, producing blank placeholders.
 */
export async function decodeAllImages(
  element: HTMLElement
): Promise<void> {
  const images = element.querySelectorAll("img");
  await Promise.all(
    Array.from(images).map(async (img) => {
      try {
        await img.decode();
      } catch {
        // ignore — image may be broken or zero-size
      }
    })
  );
}

/**
 * Build font-embed CSS once from a reference element. Pass the returned string
 * as the `fontEmbedCSS` option to html-to-image's toPng/toJpeg so it doesn't
 * re-fetch font files for every call.
 */
let cachedFontCSS: string | null = null;

export async function getSharedFontEmbedCSS(
  referenceElement: HTMLElement
): Promise<string> {
  if (cachedFontCSS) return cachedFontCSS;
  cachedFontCSS = await getFontEmbedCSS(referenceElement);
  return cachedFontCSS;
}

export type ToPngReliableResult = {
  dataUrl: string;
  attempts: number;
};

/**
 * Wait for N animation frames. Each frame gives the browser an
 * opportunity to composite and rasterize content.
 */
function waitFrames(n: number): Promise<void> {
  return new Promise((resolve) => {
    let remaining = n;
    const tick = () => {
      remaining--;
      if (remaining <= 0) resolve();
      else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/**
 * Render an SVG data URL to a canvas PNG, using `decode()` to wait for
 * the SVG image (including foreignObject content) to be fully ready.
 *
 * html-to-image's built-in pipeline uses `img.onload` which fires before
 * embedded images inside SVG foreignObject are decoded on iOS WebKit.
 * Using `img.decode()` provides a stronger guarantee that all image data
 * is rasterised before we draw to canvas.
 */
async function svgToCanvas(
  svgDataUrl: string,
  canvasWidth: number,
  canvasHeight: number
): Promise<HTMLCanvasElement> {
  const img = new Image();
  // Must use the data URL directly (not a Blob URL) — SVGs with
  // foreignObject loaded via Blob URLs taint the canvas.
  img.src = svgDataUrl;

  // decode() resolves when the image is fully decoded and ready to
  // paint — unlike onload, which fires as soon as headers are parsed.
  await img.decode();

  // Extra frame waits give WebKit time to finish compositing any
  // inner foreignObject resources that decode() didn't cover.
  await waitFrames(3);

  const canvas = document.createElement("canvas");
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
  return canvas;
}

/**
 * Reliable DOM-to-PNG that works around iOS WebKit's SVG foreignObject
 * image rendering race condition.
 *
 * html-to-image's `toPng` uses `img.onload` to know when to draw, but
 * `onload` on the outer SVG image fires before the embedded `<img>` tags
 * inside foreignObject are fully decoded. This causes images to randomly
 * appear blank.
 *
 * Our approach:
 * 1. Use html-to-image's `toSvg` for DOM cloning + serialisation (the
 *    part that works fine).
 * 2. Render the SVG to canvas ourselves using `img.decode()` instead of
 *    `onload`, which provides a stronger readiness guarantee.
 * 3. Add animation frame waits after decode for extra safety.
 * 4. Retry if the output is suspiciously small (images dropped).
 */
export async function toPngReliable(
  element: HTMLElement,
  options: {
    quality?: number;
    pixelRatio?: number;
    width?: number;
    height?: number;
    fontEmbedCSS?: string;
  },
  reliabilityOptions?: {
    /** Max render attempts. Default: 3. */
    maxAttempts?: number;
    /** Callback for diagnostic logging. */
    onAttempt?: (attempt: number, dataUrlLength: number) => void;
  }
): Promise<ToPngReliableResult> {
  const { maxAttempts = 3, onAttempt } = reliabilityOptions ?? {};
  const pixelRatio = options.pixelRatio ?? 1;
  const width = options.width ?? element.scrollWidth;
  const height = options.height ?? element.scrollHeight;
  const canvasWidth = width * pixelRatio;
  const canvasHeight = height * pixelRatio;

  // Step 1: Serialize DOM to SVG (clone, inline styles/images/fonts)
  const svgDataUrl = await toSvg(element, { ...options, width, height });

  // Step 2: Render SVG to canvas with proper decode() waiting.
  // Retry if the output is suspiciously small (images were dropped).
  let bestDataUrl = "";
  let bestLength = 0;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const canvas = await svgToCanvas(svgDataUrl, canvasWidth, canvasHeight);
    const dataUrl = canvas.toDataURL("image/png");
    onAttempt?.(attempt, dataUrl.length);

    if (dataUrl.length > bestLength) {
      bestDataUrl = dataUrl;
      bestLength = dataUrl.length;
    }

    // Accept if output grew from the previous attempt by less than 5%
    // (meaning subsequent renders aren't finding new content to decode).
    if (attempt > 1 && dataUrl.length >= bestLength * 0.95) {
      return { dataUrl: bestDataUrl, attempts: attempt };
    }

    // On first attempt, always try at least one more to compare.
    if (attempt < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }

  return { dataUrl: bestDataUrl, attempts: maxAttempts };
}
